import { twJoin } from 'tailwind-merge';

import { ProfileSectionHeader } from '@/components/profile/ProfileSectionHeader';
import { ProfileItemMeta, ProfileItemTitle } from '@/components/profile/ProfileText';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';

interface EducationItemProps {
  text: string;
  title: string;
  index: number;
  totalItems: number;
}

const EducationItem = ({ text, title, index, totalItems }: EducationItemProps) => {
  const [degree, institution] = text.split('\n');

  return (
    <VStack
      className={twJoin(
        'mx-auto w-full max-w-5xl items-start px-6',
        totalItems - 1 !== index ? 'mb-4' : ''
      )}
      key={title}>
      <ProfileItemMeta className="mb-1 leading-none">{title}</ProfileItemMeta>
      {degree ? <ProfileItemTitle className="mb-1 leading-tight">{degree}</ProfileItemTitle> : null}
      {institution ? <ProfileItemMeta className="mb-1">{institution}</ProfileItemMeta> : null}
    </VStack>
  );
};

interface EducationSectionProps {
  section: {
    title: string;
    data: readonly any[];
  };
  isFirstSection: boolean;
  isOwnProfile?: boolean;
  onEdit?: () => void;
}

/**
 * EducationSection - Profile education display component
 *
 * Renders a user's education history with individual education items.
 * Features include:
 * - Parses education text to separate degree and institution
 * - Conditional edit button for profile owners
 * - Responsive layout with proper spacing
 * - Individual education item rendering with date ranges
 */
export const EducationSection = ({ section, isOwnProfile, onEdit }: EducationSectionProps) => {
  return (
    <View>
      <ProfileSectionHeader title={section.title} isOwnProfile={isOwnProfile} onEdit={onEdit} />
      {section.data.map((item, index) => (
        <EducationItem
          key={`${item.title}-${index}`}
          text={item.text || ''}
          title={item.title}
          index={index}
          totalItems={section.data.length}
        />
      ))}
    </View>
  );
};
